import React, { useEffect } from "react"
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import InfoIcon from "@material-ui/icons/Info"
import SaveIcon from "@material-ui/icons/Save"
import { useSnackbar } from "../../../utils/notificacoes"
import type { CampoRespostaIzza } from "../models/respostasPersonalizadasIzzaModel"
import { RespostasPersonalizadasIzzaActionsTypes } from "../context/respostasPersonalizadasIzzaActions"
import { useRespostasPersonalizadasIzzaContext } from "../context/useRespostasPersonalizadasIzzaContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    campoContainer: {
        marginBottom: theme.spacing(3),
    },
    labelRow: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(0.5),
        gap: theme.spacing(1),
    },
    labelText: {
        fontWeight: 500,
        color: "#2c3e50",
    },
    infoIcon: {
        color: "#7f8c8d",
        fontSize: 18,
        cursor: "help",
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        boxShadow: "0 4px 15px rgba(var(--cor-botao-salvar-rgb),0.3)",
        padding: theme.spacing(1.2, 4),
        "&:hover": {
            backgroundColor: "var(--cor-botao-salvar-hover)",
            boxShadow: "0 6px 20px rgba(var(--cor-botao-salvar-rgb),0.4)",
        },
    },
}))

interface CampoProps {
    campo: CampoRespostaIzza
    label: string
    dica?: string
}

function CampoResposta({ campo, label, dica }: CampoProps) {
    const classes = useStyles()
    const { dados, dispatch } = useRespostasPersonalizadasIzzaContext()
    const item = dados[campo]

    return (
        <Box className={classes.campoContainer}>
            <Box className={classes.labelRow}>
                <Typography variant="body1" className={classes.labelText}>
                    {label}
                </Typography>
                {dica && (
                    <Tooltip title={dica} placement="right">
                        <InfoIcon className={classes.infoIcon} />
                    </Tooltip>
                )}
            </Box>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={item.ativo}
                        onChange={() =>
                            dispatch({
                                type: RespostasPersonalizadasIzzaActionsTypes.TOGGLE_ATIVO,
                                payload: { campo },
                            })
                        }
                        style={{ color: "#4caf50" }}
                        size="small"
                    />
                }
                label="Ativo"
            />
            <TextField
                variant="outlined"
                fullWidth
                multiline
                rows={5}
                value={item.resposta}
                onChange={e =>
                    dispatch({
                        type: RespostasPersonalizadasIzzaActionsTypes.ATUALIZAR_RESPOSTA,
                        payload: { campo, resposta: e.target.value },
                    })
                }
                size="small"
            />
        </Box>
    )
}

export function RespostasPersonalizadasIzzaComponent() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, erroCarregamento, salvar } = useRespostasPersonalizadasIzzaContext()
    const { enqueueSnackbar } = useSnackbar()
    const { modoIzza } = dados

    const modoMenu = modoIzza === 5
    const modoCompleta = modoIzza === 2

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento])

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant={isMobile ? "h5" : "h4"} className={classes.titleText} gutterBottom>
                    Respostas Personalizadas Izza
                </Typography>
                <Typography variant="body2" style={{ color: "#7f8c8d", marginBottom: 24 }}>
                    Cadastre e edite as respostas automáticas da Izza.
                </Typography>

                {!modoMenu && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Notificações e Boas-vindas
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <CampoResposta
                                campo="boasVindas"
                                label="Boas Vindas"
                                dica="$cardapio — link do cardápio | $cliente — nome do cliente | $promocao — promoções de fidelidade"
                            />
                            <CampoResposta
                                campo="aceito"
                                label="Notificação Pedido Aceito (WhatsApp)"
                                dica="$pedido — conteúdo do pedido (itens) | $cliente — nome do cliente"
                            />
                            <CampoResposta
                                campo="retiradoBalcao"
                                label="Notificação Retirada no Balcão"
                                dica="$pedido — id do pedido | $cliente — nome do cliente"
                            />
                            <CampoResposta
                                campo="saiuParaEntrega"
                                label="Notificação Saiu para Entrega"
                                dica="$pedido — id do pedido | $cliente — nome do cliente"
                            />
                            <CampoResposta
                                campo="emProducao"
                                label="Notificação Em Preparação (Delivery)"
                                dica="$pedido — id do pedido | $cliente — nome do cliente"
                            />
                            <CampoResposta
                                campo="emProducaoRetirada"
                                label="Notificação Em Preparação (Retirada)"
                                dica="$pedido — id do pedido | $cliente — nome do cliente"
                            />
                            <CampoResposta
                                campo="entregue"
                                label="Notificação Entregue"
                                dica="$pedido — id do pedido | $cliente — nome do cliente | $avaliacao — link de avaliação"
                            />
                        </Box>
                    </Paper>
                )}

                {modoCompleta && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Respostas Automáticas (Modo Completo)
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <CampoResposta campo="horarioFuncionamento" label="Horário de Funcionamento" />
                            <CampoResposta
                                campo="taxaEntrega"
                                label="Taxa de Entrega"
                                dica="As taxas de cada localidade devem ser inseridas manualmente. AVISO: A personalização desta mensagem NÃO é indicada."
                            />
                            <CampoResposta campo="contato" label="Contato" />
                            <CampoResposta
                                campo="cardapio"
                                label="Cardápio"
                                dica="$cardapio — link do cardápio"
                            />
                            <CampoResposta
                                campo="formaPagamento"
                                label="Forma de Pagamento"
                                dica="$pagamento — todas as formas de pagamento aceitas"
                            />
                            <CampoResposta
                                campo="tempoEntrega"
                                label="Tempo de Entrega"
                                dica="$tempo — tempo de entrega do estabelecimento"
                            />
                            <CampoResposta
                                campo="situacaoPedidoNormal"
                                label="Situação do Pedido"
                                dica="$previsao — previsão de entrega | $status — status do pedido"
                            />
                            <CampoResposta
                                campo="situacaoPedidoAtrasado"
                                label="Situação do Pedido Atrasado"
                                dica="$previsao — previsão de entrega | $status — status do pedido"
                            />
                            <CampoResposta campo="cancelamento" label="Solicitação de Cancelamento" />
                            <CampoResposta campo="alteracao" label="Solicitar Alteração" />
                        </Box>
                    </Paper>
                )}

                <Box display="flex" justifyContent="center" mt={2}>
                    <Button
                        variant="contained"
                        className={classes.saveButton}
                        startIcon={<SaveIcon />}
                        onClick={salvar}
                    >
                        Salvar
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
